/**
 * Die Warteschlange der Einstellungs-Änderungen, die der Server noch nicht
 * bestätigt hat. (23.09.2026, Ticket 2511)
 *
 * Martin: *„Wer etwas ändert, schreibt zuerst auf den Server und bekommt eine
 * Bestätigung. Schlägt das fehl, zeigt die App das sichtbar an, statt es
 * still zu verschlucken."*
 *
 * ## Warum eine eigene Datei
 *
 * `saveSettings` muss die Änderung SOFORT und SYNCHRON festhalten. Vorher
 * lief der Weg zum Server über einen dynamischen Import „irgendwann danach";
 * schloss man die App im selben Augenblick, war die Änderung nur noch im
 * Zwischenspeicher dieses Geräts — und kein anderes Gerät erfuhr je davon.
 * Hier steht nur Speicher, kein Netz: settings.ts und appearance.ts dürfen
 * das statisch laden, ohne einen Kreis zu bilden.
 *
 * ⚠️ Die Schlange trägt die Nutzerkennung. Nach einem Kontowechsel auf
 *    demselben Gerät gehen die offenen Änderungen des Vorgängers NIE an das
 *    neue Konto (einstellungen-abgleich.ts verwirft sie).
 */

const SCHLUESSEL = "voice-v8.settings-ausstehend";

/**
 * Wie eine Änderung ausgegangen ist.
 *   speichert   unterwegs zum Server
 *   gespeichert vom Server bestätigt
 *   geraet      ein Gerätewert — gilt hier, sobald er im Speicher steht
 *   wartet      kein Netz; bleibt vorgemerkt und wird nachgereicht
 *   fehler      der Server lehnt ab oder antwortet mit einem Fehler
 *   ueberholt   ein anderes Gerät hat später geändert; dessen Wert gilt
 */
export type SyncZustand =
  | "speichert"
  | "gespeichert"
  | "geraet"
  | "wartet"
  | "fehler"
  | "ueberholt";

export interface SpeicherErgebnis {
  zustand: SyncZustand;
  felder: string[];
  /** Was die Oberfläche sagt — leer, wenn es nichts zu sagen gibt. */
  text: string;
  status?: number;
}

/**
 * Die Worte für jeden Ausgang — EINE Stelle, damit Einstellungsseite und
 * Hinweis am Rand dasselbe sagen.
 */
export function ergebnis(
  zustand: SyncZustand,
  felder: string[],
  opts: { status?: number; abgelehnt?: boolean } = {},
): SpeicherErgebnis {
  let text = "";
  if (zustand === "wartet") {
    text = "Nicht gespeichert: kein Netz. Wird nachgereicht.";
  } else if (zustand === "ueberholt") {
    text = "Ein anderes Gerät hat das inzwischen geändert — dessen Wert gilt.";
  } else if (zustand === "fehler") {
    if (opts.abgelehnt) {
      text = "Nicht gespeichert: Der Server nimmt diesen Wert nicht an.";
    } else if (opts.status === 401 || opts.status === 403) {
      text = "Nicht gespeichert: Die Anmeldung ist abgelaufen. Nach dem Anmelden wird es nachgereicht.";
    } else {
      text = `Nicht gespeichert: Der Server antwortet mit Fehler ${opts.status ?? "?"}. Wird erneut versucht.`;
    }
  }
  return { zustand, felder, text, ...(opts.status !== undefined ? { status: opts.status } : {}) };
}

export interface AusstehendesFeld {
  wert: unknown;
  /** Wann das Gerät die Änderung gemacht hat — auf SEINER Uhr. */
  geaendertAm: number;
  /** Nur übernehmen, wenn der Server das Feld noch nicht kennt (Migration). */
  nurWennFehlt?: boolean;
}

interface Ausstehend {
  /** Wem die Änderungen gehören; null = vor der Anmeldung gemacht. */
  uid: number | null;
  felder: Record<string, AusstehendesFeld>;
}

/**
 * Wann ein Feld zuletzt auf diesem Gerät geändert wurde (nur im Speicher).
 * Der Abgleich fragt das, bevor er einen Serverwert übernimmt: Eine Antwort,
 * die unterwegs war, während der Nutzer umschaltete, darf seine Wahl nicht
 * zurückdrehen (derselbe Rennschutz wie 2026-07-08, jetzt je Feld).
 */
const zuletztLokal = new Map<string, number>();

/**
 * Streikt der Browser-Speicher (Privatmodus, voll), hält die Schlange
 * wenigstens für diese Sitzung — sonst erreichte eine Änderung den Server
 * nie. Vorher schickte der alte Weg das Objekt direkt, am Speicher vorbei.
 */
let ersatz: Ausstehend | null = null;

export function liesAusstehend(): Ausstehend {
  if (ersatz) return { uid: ersatz.uid, felder: { ...ersatz.felder } };
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    if (!roh) return { uid: null, felder: {} };
    const p = JSON.parse(roh) as Partial<Ausstehend>;
    const felder =
      p.felder && typeof p.felder === "object" && !Array.isArray(p.felder)
        ? p.felder
        : {};
    return { uid: typeof p.uid === "number" ? p.uid : null, felder };
  } catch {
    return { uid: null, felder: {} };
  }
}

function schreibe(stand: Ausstehend): void {
  try {
    if (Object.keys(stand.felder).length === 0 && stand.uid === null) {
      localStorage.removeItem(SCHLUESSEL);
    } else {
      localStorage.setItem(SCHLUESSEL, JSON.stringify(stand));
    }
    ersatz = null;
  } catch {
    ersatz = stand;
  }
}

/** Änderungen vormerken. Gibt die vorgemerkten Feldnamen zurück. */
export function merkeAenderungen(
  werte: Record<string, unknown>,
  opts: { nurWennFehlt?: boolean; geaendertAm?: number } = {},
): string[] {
  const stand = liesAusstehend();
  const jetzt = opts.geaendertAm ?? Date.now();
  const gemerkt: string[] = [];
  for (const [feld, wert] of Object.entries(werte)) {
    // Eine echte Änderung wird nie von einer Migrations-Übernahme verdrängt.
    if (opts.nurWennFehlt && stand.felder[feld] && !stand.felder[feld].nurWennFehlt) continue;
    stand.felder[feld] = {
      wert,
      geaendertAm: jetzt,
      ...(opts.nurWennFehlt ? { nurWennFehlt: true } : {}),
    };
    if (!opts.nurWennFehlt) zuletztLokal.set(feld, jetzt);
    gemerkt.push(feld);
  }
  schreibe(stand);
  return gemerkt;
}

/**
 * Bestätigte Felder austragen — aber nur, wenn seit dem Senden nicht schon
 * wieder geändert wurde (sonst ginge die neuere Änderung verloren).
 */
export function trageAus(gesendet: Record<string, AusstehendesFeld>, felder: string[]): void {
  const stand = liesAusstehend();
  for (const feld of felder) {
    const jetzt = stand.felder[feld];
    const damals = gesendet[feld];
    if (!jetzt || !damals) continue;
    // Beides vergleichen: Zwei Änderungen in derselben Millisekunde (ein
    // Regler) tragen denselben Stempel, aber nicht denselben Wert.
    if (
      jetzt.geaendertAm === damals.geaendertAm &&
      JSON.stringify(jetzt.wert) === JSON.stringify(damals.wert)
    ) {
      delete stand.felder[feld];
    }
  }
  schreibe(stand);
}

/** Die Schlange einem Konto zuordnen (vor der Anmeldung gemachte Änderungen). */
export function bindeAn(uid: number): void {
  const stand = liesAusstehend();
  if (stand.uid === uid) return;
  schreibe({ uid, felder: stand.felder });
}

/** Alles verwerfen (fremdes Konto). */
export function verwerfeAusstehend(): void {
  ersatz = null;
  try {
    localStorage.removeItem(SCHLUESSEL);
  } catch {
    /* egal */
  }
}

export function istAusstehend(feld: string): boolean {
  return liesAusstehend().felder[feld] !== undefined;
}

/**
 * Wer die Vormerkungen an den Server bringt. einstellungen-abgleich.ts trägt
 * sich beim Laden hier ein — so kennen settings.ts und appearance.ts den
 * Netzweg nicht und bilden keinen Import-Kreis mit ihm.
 */
type Sender = (felder: string[]) => Promise<SpeicherErgebnis>;
let sender: Sender | null = null;

export function setzeSender(s: Sender): void {
  sender = s;
}

/**
 * Das Vorgemerkte senden. Ist der Abgleich (noch) nicht geladen, bleibt es
 * vorgemerkt — starteAbgleich reicht es nach.
 */
export function sendeVorgemerktes(felder: string[]): Promise<SpeicherErgebnis> {
  if (!sender) return Promise.resolve(ergebnis("wartet", felder));
  return sender(felder).catch(() => ergebnis("wartet", felder));
}

/** Wurde das Feld auf diesem Gerät seit `seit` (ms) geändert? */
export function lokalGeaendertSeit(feld: string, seit: number): boolean {
  return (zuletztLokal.get(feld) ?? -Infinity) >= seit;
}
